import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  modalityTerms,
  parametrization,
  type ComplementaryModalityLink,
  type ModalityDetail,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDate } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { DataTable } from '@/shared/ui/DataTable';
import { DetailCard } from '@/shared/ui/DetailLayout';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Select } from '@/shared/ui/Select';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../shared/fieldError';
import { campoRecusado } from './textos';

/**
 * Secao "Modalidades Complementares": as modalidades que a principal permite como
 * complementar (Core/GetComplementaryModalities), com a inclusao e a ativacao.
 *
 * No OnPoint o item aparece desabilitado quando a modalidade nao marca "permite a
 * inclusao de modalidade complementar" (a condicao e a confirmar). Aqui a secao abre
 * sempre, para que o que ja foi incluido continue visivel, e o aviso diz o que a
 * versao em vigor marca: a marcacao e guardada sem efeito ate MOD-005.
 */
export function ModalidadesComplementares({ detalhe }: { detalhe: ModalityDetail }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const podeIncluir = can('parametrizacao.modalidade.create');
  const podeEditar = can('parametrizacao.modalidade.update');
  const [escolhida, setEscolhida] = useState('');
  const chave = ['complementary-modalities', detalhe.id];

  const consulta = useQuery({
    queryKey: chave,
    queryFn: ({ signal }) =>
      modalityTerms.listComplementary(detalhe.id, { include_inactive: true }, signal),
  });
  const modalidades = useQuery({
    queryKey: ['modalities', 'complementares'],
    queryFn: ({ signal }) =>
      parametrization.listModalities({ limit: 100, sort: 'name' }, signal),
    enabled: podeIncluir,
  });

  const incluir = useMutation({
    mutationFn: () => modalityTerms.createComplementary(detalhe.id, escolhida),
    onSuccess: () => {
      setEscolhida('');
      toast.success('Modalidade complementar incluida.');
      void queryClient.invalidateQueries({ queryKey: chave });
    },
  });
  const situacao = useMutation({
    mutationFn: (v: ComplementaryModalityLink) =>
      modalityTerms.updateComplementary(
        detalhe.id,
        v.id,
        v.status === 'ativo' ? 'inativo' : 'ativo',
      ),
    onSuccess: (v) => {
      toast.success(
        v.status === 'ativo' ? 'Complementar ativada.' : 'Complementar desativada.',
      );
      void queryClient.invalidateQueries({ queryKey: chave });
    },
    onError: (erro) => toast.error(erro.message),
  });
  const apiError = incluir.error instanceof ApiError ? incluir.error : null;

  const jaIncluidas = new Set(
    (consulta.data ?? []).map((v) => v.complementary_modality_id),
  );
  const opcoes = (modalidades.data?.items ?? [])
    .filter((m) => m.id !== detalhe.id && !jaIncluidas.has(m.id))
    .map((m) => ({ value: m.id, label: m.name }));
  const permite = detalhe.active_setting?.values.allows_complementary ?? false;

  return (
    <DetailCard
      title="Modalidades Complementares"
      description="As modalidades que podem entrar como complementar desta, como a Manutencao Corretiva para Obras, Fornecimento e Prestacao de Servicos."
      loading={consulta.isPending}
      error={consulta.error}
    >
      <Alert tone="info" title="Regra pendente">
        {permite
          ? 'A versao em vigor marca que a modalidade permite a inclusao de modalidade complementar.'
          : 'A versao em vigor nao marca que a modalidade permite a inclusao de modalidade complementar.'}{' '}
        A lista e guardada como parametro: a cotacao ainda nao oferece nem confere a
        complementar por ela (MOD-004 e MOD-005).
      </Alert>

      {podeIncluir ? (
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(evento) => {
            evento.preventDefault();
            if (escolhida) incluir.mutate();
          }}
        >
          <div className="min-w-64 flex-1">
            <Select
              label="Modalidade complementar"
              options={opcoes}
              placeholder="Escolha a modalidade"
              value={escolhida}
              error={
                fieldErrorOf(apiError, 'complementary_modality_id') ??
                (incluir.error && !campoRecusado(apiError)
                  ? incluir.error.message
                  : undefined)
              }
              onChange={(e) => setEscolhida(e.target.value)}
            />
          </div>
          <Button type="submit" loading={incluir.isPending} disabled={!escolhida}>
            Incluir complementar
          </Button>
        </form>
      ) : null}

      <DataTable
        columns={[
          {
            key: 'modalidade',
            header: 'Modalidade',
            render: (v: ComplementaryModalityLink) => (
              <div className="flex flex-col">
                <span className="font-medium text-ink-900">{v.complementary.name}</span>
                <span className="text-2xs text-ink-500">
                  {v.complementary.product_name}
                </span>
              </div>
            ),
          },
          {
            key: 'situacao-modalidade',
            header: 'Situacao da modalidade',
            render: (v: ComplementaryModalityLink) => (
              <StatusBadge kind="cadastro" value={v.complementary.status} />
            ),
          },
          {
            key: 'cadastro',
            header: 'Data de cadastro',
            render: (v: ComplementaryModalityLink) => formatDate(v.created_at),
          },
          {
            key: 'status',
            header: 'Status',
            render: (v: ComplementaryModalityLink) => (
              <StatusBadge kind="cadastro" value={v.status} />
            ),
          },
          {
            key: 'acoes',
            header: 'Acoes',
            render: (v: ComplementaryModalityLink) =>
              podeEditar ? (
                <Button
                  size="sm"
                  variant="ghost"
                  loading={situacao.isPending && situacao.variables?.id === v.id}
                  onClick={() => situacao.mutate(v)}
                >
                  {v.status === 'ativo' ? 'Desativar' : 'Ativar'}
                </Button>
              ) : (
                '-'
              ),
          },
        ]}
        items={consulta.data ?? []}
        keyOf={(v) => v.id}
        empty={
          <EmptyState icon="layers" title="Nenhuma modalidade complementar ainda" />
        }
      />
    </DetailCard>
  );
}
